import React, { useState } from 'react';
import { Send, X, Paperclip, User, Mail, Search, CheckCircle } from 'lucide-react';
import { config } from '../../config';

const EmailComposer = ({ isOpen, onClose, initialData = {} }) => {
    const [to, setTo] = useState(initialData.to || '');
    const [subject, setSubject] = useState(initialData.subject || '');
    const [body, setBody] = useState(initialData.body || '');
    const [isSending, setIsSending] = useState(false);
    const [success, setSuccess] = useState(false);

    if (!isOpen) {return null;}

    const handleSend = async (e) => {
        e.preventDefault();
        setIsSending(true);
        try {
            const res = await fetch(`${config.API_BASE_URL}/webhook/send-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    to,
                    subject,
                    body,
                    senderEmail: localStorage.getItem('USER_EMAIL')
                })
            });
            if (res.ok) {
                setSuccess(true);
                setTimeout(() => {
                    onClose();
                    setSuccess(false);
                }, 2000);
            }
        } catch (error) {
            console.error("Email Error", error);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden relative border border-slate-100 flex flex-col h-[80vh]">

                {/* Header */}
                <div className="p-6 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="bg-rose-600 p-2 rounded-xl text-white shadow-lg shadow-rose-200">
                            <Mail size={20} />
                        </div>
                        <div>
                            <h3 className="font-black text-slate-800 uppercase tracking-tight">New Message</h3>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none mt-1">Universal Email Hub</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400">
                        <X size={20} />
                    </button>
                </div>

                {/* Form */}
                <div className="flex-1 overflow-y-auto p-8">
                    {!success ? (
                        <form onSubmit={handleSend} className="space-y-6">
                            <div className="space-y-4">
                                <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100 focus-within:ring-2 focus-within:ring-rose-500 transition-all">
                                    <User size={18} className="text-slate-400" />
                                    <input
                                        type="email"
                                        placeholder="Recipient Email"
                                        className="bg-transparent flex-1 outline-none text-sm font-bold text-slate-700"
                                        value={to}
                                        onChange={(e) => setTo(e.target.value)}
                                        required
                                    />
                                    <Search size={16} className="text-slate-300 pointer-events-none" />
                                </div>

                                <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100 focus-within:ring-2 focus-within:ring-rose-500 transition-all">
                                    <Mail size={18} className="text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Subject"
                                        className="bg-transparent flex-1 outline-none text-sm font-bold text-slate-700"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="bg-slate-50 rounded-[2rem] border border-slate-100 focus-within:ring-2 focus-within:ring-rose-500 transition-all h-[40vh] flex flex-col overflow-hidden">
                                    <textarea
                                        placeholder="Write your message here..."
                                        className="bg-transparent flex-1 p-6 outline-none text-sm font-medium text-slate-600 leading-relaxed resize-none"
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        required
                                    />
                                    <div className="p-4 bg-white border-t border-slate-100 flex items-center justify-between">
                                        <button type="button" className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-xl transition-all flex items-center gap-2 text-[10px] font-black uppercase tracking-widest">
                                            <Paperclip size={16} />
                                            Add Attachment
                                        </button>
                                        <div className="flex gap-2">
                                            {body.length > 0 && <span className="text-[10px] font-bold text-slate-300 uppercase tracking-tighter">{body.length} characters</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isSending}
                                className="w-full bg-slate-900 text-white py-5 rounded-2xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-3 hover:bg-slate-800 disabled:opacity-50 transition-all shadow-xl shadow-slate-200 active:scale-95"
                            >
                                {isSending ? (
                                    <span className="animate-pulse">Sending Signal...</span>
                                ) : (
                                    <>
                                        <Send size={18} />
                                        Send Message
                                    </>
                                )}
                            </button>
                        </form>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center py-12 animate-in zoom-in-95 duration-500">
                            <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6 scale-110 shadow-lg shadow-green-100">
                                <CheckCircle size={56} />
                            </div>
                            <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Signal Sent!</h3>
                            <p className="text-slate-500 text-sm font-medium mt-2">Your email has been dispatched and logged.</p>
                        </div>
                    )}
                </div>

                <div className="p-6 text-center border-t border-slate-50">
                    <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest">
                        Bidirectional Secure Email Gateway • BizBuilder Communicator
                    </p>
                </div>
            </div>
        </div>
    );
};

export default EmailComposer;
